import type React from "react";
import { useState, useEffect } from "react";
import { Calendar, ChevronDown, Search, X, Filter } from "lucide-react";
import { useFilter } from "../../../context/FilterContext";
import { payments_status, settlement_status } from "../../../utils/utils";
import { useLocation } from "react-router-dom";

interface FiltersProps {
  searchPlaceholder?: string;
  showDateFilters?: boolean;
  showTypeFilter?: boolean;
  showSearch?: boolean;
  section?: string;
  from?: string;
}

const Filters: React.FC<FiltersProps> = ({
  searchPlaceholder = "Reference",
  showDateFilters = true,
  showTypeFilter = true,
  showSearch = true,
  section = "payments",
  from = "none",
}) => {
  const {
    startDate,
    endDate,
    type,
    searchQuery,
    setStartDate,
    setEndDate,
    setType,
    setSearchQuery,
    resetFilters,
    displayStartDate,
    displayEndDate,
  } = useFilter();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const location = useLocation();
  const isSettlementsPage = location.pathname.includes("settlements");

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearchQuery(value);
    }, 500);

    setSearchTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const handleTypeSelect = (selectedType: string) => {
    setType(selectedType);
    setIsTypeOpen(false);
  };

  const hasActiveFilters = startDate || endDate || type || searchQuery;

  const getStatusOptions = () => {
    return isSettlementsPage ? settlement_status : payments_status;
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-100">
      {/* Desktop Filters */}
      <div className="hidden md:flex items-center p-4 gap-4 flex-wrap">
        {showSearch && (
          <div className="relative flex-grow min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder={`Search by ${searchPlaceholder}`}
              value={localSearchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setLocalSearchQuery("");
                  setSearchQuery("");
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {showDateFilters && (
          <>
            <div className="relative">
              <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-300">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <input
                  type="date"
                  value={displayStartDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="outline-none text-sm w-full"
                  placeholder="Start Date"
                />
              </div>
            </div>

            <div className="relative">
              <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-300">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <input
                  type="date"
                  value={displayEndDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="outline-none text-sm w-full"
                  placeholder="End Date"
                />
              </div>
            </div>
          </>
        )}
        {showTypeFilter && section !== "customers" && (
          <div className="relative">
            <button
              onClick={() => setIsTypeOpen(!isTypeOpen)}
              className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 min-w-[150px] focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            >
              <span className="text-sm">{type || "Select Type"}</span>
              <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
            </button>

            {isTypeOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {getStatusOptions().map((status) => (
                  <button
                    key={status}
                    onClick={() => handleTypeSelect(status)}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="text-sm text-blue-500 hover:text-blue-700 flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </button>
        )}
      </div>

      {/* Mobile Filters */}
      <div className="md:hidden p-4">
        <div className="flex items-center gap-2">
          {showSearch && (
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={`Search by ${searchPlaceholder}`}
                value={localSearchQuery}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setLocalSearchQuery("");
                    setSearchQuery("");
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
            className="flex items-center justify-center p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {isMobileFilterOpen && (
          <div className="mt-4 space-y-3 border-t pt-3 border-gray-100">
            {showDateFilters && (
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <input
                      type="date"
                      value={displayStartDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="outline-none text-sm w-full"
                      placeholder="Start Date"
                    />
                  </div>
                </div>

                <div className="relative">
                  <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <input
                      type="date"
                      value={displayEndDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="outline-none text-sm w-full"
                      placeholder="End Date"
                    />
                  </div>
                </div>
              </div>
            )}

            {showTypeFilter && (
              <div className="relative">
                <button
                  onClick={() => setIsTypeOpen(!isTypeOpen)}
                  className="flex items-center justify-between w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none"
                >
                  <span className="text-sm">{type || "Select Type"}</span>
                  <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
                </button>

                {isTypeOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                    {getStatusOptions().map((status) => (
                      <button
                        key={status}
                        onClick={() => handleTypeSelect(status)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none"
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-sm text-blue-500 hover:text-blue-700 flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Filters;
